// Subpath import on purpose: the reporter is installed by main.ts before the game loads, and the
// '@towerpower/shared/content' entry would pull its whole domain into the eagerly loaded entry chunk.
import {
  CLIENT_ERROR_MESSAGE_MAX_LENGTH,
  CLIENT_ERROR_STACK_MAX_LENGTH,
  CLIENT_ERROR_URL_MAX_LENGTH,
  USER_AGENT_MAX_LENGTH,
  type ClientErrorKind,
} from '@towerpower/shared/client-error-reporting';
import { BUILD_ID } from '../buildId';

const CLIENT_ERROR_ENDPOINT = '/api/client-errors';

/**
 * One page session's budget. A throw inside a render loop fires `error` once per frame, so the cap —
 * not the dedupe — is what keeps a broken build from turning into a request storm against the API.
 */
export const MAX_REPORTS_PER_PAGE_SESSION = 10;

export interface ClientErrorReporter {
  /** Adds the window `error` and `unhandledrejection` listeners. Calling it again is a no-op. */
  install(): void;
  /** Removes those listeners again; `report` keeps working afterwards. */
  uninstall(): void;
  /** True when the report was sent; false when it was a duplicate or the session budget was spent. */
  report(kind: ClientErrorKind, message: string, stack?: string): boolean;
}

/** The server caps these, so an oversized field must be cut here rather than lose the whole report. */
function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

function reasonText(reason: unknown): string {
  if (typeof reason === 'string') return reason;
  try {
    return JSON.stringify(reason) ?? String(reason);
  } catch {
    return String(reason);
  }
}

function describeReason(reason: unknown): { message: string; stack?: string } {
  if (reason instanceof Error) {
    return { message: reason.message || reason.name, stack: reason.stack };
  }
  return { message: reasonText(reason) };
}

/** Shared with main.ts's bootstrap catch so both report a thrown value the same way. */
export function describeErrorReason(reason: unknown): { message: string; stack?: string } {
  return describeReason(reason);
}

/** Only the path is sent: a query or hash can carry session state a log should not hold. */
function currentUrlPath(): string {
  const href = globalThis.location?.href ?? '';
  const queryStart = href.search(/[?#]/);
  return truncate(queryStart === -1 ? href : href.slice(0, queryStart), CLIENT_ERROR_URL_MAX_LENGTH);
}

function currentUserAgent(): string {
  return truncate(globalThis.navigator?.userAgent ?? '', USER_AGENT_MAX_LENGTH);
}

function send(body: string): void {
  const url = `${import.meta.env.VITE_API_URL || ''}${CLIENT_ERROR_ENDPOINT}`;
  const beacon = globalThis.navigator?.sendBeacon;
  if (typeof beacon === 'function') {
    // A string body would go out as text/plain, which the API's JSON parser rejects; a typed Blob
    // is the only way sendBeacon can set the content type.
    if (beacon.call(globalThis.navigator, url, new Blob([body], { type: 'application/json' }))) return;
  }
  // keepalive so the report survives the navigation or teardown that a fatal error usually causes.
  void fetch(url, {
    method: 'POST',
    body,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    keepalive: true,
  }).catch(() => undefined);
}

export function createClientErrorReporter(): ClientErrorReporter {
  const reportedKeys = new Set<string>();
  let sent = 0;
  let installed = false;
  // Set while a handler runs, so an error the reporter itself causes is dropped instead of re-entering.
  let handling = false;

  function report(kind: ClientErrorKind, message: string, stack?: string): boolean {
    const text = truncate(message || 'Unknown error', CLIENT_ERROR_MESSAGE_MAX_LENGTH);
    const key = `${kind}\u0000${text}`;
    if (reportedKeys.has(key)) return false;
    if (sent >= MAX_REPORTS_PER_PAGE_SESSION) return false;
    reportedKeys.add(key);
    sent += 1;

    try {
      send(JSON.stringify({
        kind,
        message: text,
        ...(stack ? { stack: truncate(stack, CLIENT_ERROR_STACK_MAX_LENGTH) } : {}),
        url: currentUrlPath(),
        buildId: BUILD_ID,
        userAgent: currentUserAgent(),
      }));
    } catch {
      // Reporting is best-effort by definition: a failed send must never become the next error.
    }
    return true;
  }

  function onError(event: ErrorEvent): void {
    if (handling) return;
    handling = true;
    try {
      const described = describeReason(event.error);
      report('error', event.message || described.message, described.stack);
    } finally {
      handling = false;
    }
  }

  function onUnhandledRejection(event: PromiseRejectionEvent): void {
    if (handling) return;
    handling = true;
    try {
      const described = describeReason(event.reason);
      report('unhandledrejection', described.message, described.stack);
    } finally {
      handling = false;
    }
  }

  return {
    install(): void {
      if (installed) return;
      installed = true;
      window.addEventListener('error', onError);
      window.addEventListener('unhandledrejection', onUnhandledRejection);
    },
    uninstall(): void {
      if (!installed) return;
      installed = false;
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    },
    report,
  };
}

/** The page-wide reporter; main.ts installs it before anything else on the page can throw. */
export const clientErrorReporter = createClientErrorReporter();
